import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Newspaper, 
  Search, 
  Plus, 
  Edit3, 
  Trash2, 
  Calendar, 
  User, 
  Clock, 
  ArrowRight, 
  Send, 
  Eye, 
  Sparkles,
  BookOpen, 
  Bell, 
  CheckCircle, 
  FileText,
  Loader2,
  X,
  Globe,
  Settings,
  Image as ImageIcon,
  Share2,
  Printer,
  Wand2,
  BarChart3,
  Users,
  Target,
  Upload
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useApiClient } from "@/hooks/use-api-client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Navigate, useParams, useNavigate, useSearchParams } from "react-router-dom";

// Rich Bulletin Components
import { RichBulletinRenderer } from "./components/RichBulletinRenderer";
import { RichEditorToolbar } from "./components/RichEditorToolbar";
import { MarketingContactsModal } from "./components/MarketingContactsModal";
import { BulletinAnalyticsModal } from "./components/BulletinAnalyticsModal";
import { UnsplashStockGalleryModal } from "./components/UnsplashStockGalleryModal";

interface Bulletin {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  image_url?: string;
  category: string;
  status: "draft" | "published";
  read_time: string;
  published_at?: string;
  created_at: string;
  updated_at: string;
  author_name: string;
  author_avatar?: string;
}

const CATEGORIES = [
  "Actualizaciones",
  "Nuevas Funciones",
  "Tutoriales",
  "Avisos"
];

const DEFAULT_COVER = "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop";

// Presets for cover gallery selection
const COVER_PRESETS = [
  { name: "Calculadoras", url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop" },
  { name: "Legal Tech", url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop" },
  { name: "Leyes y Justicia", url: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop" },
  { name: "Despachos", url: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1200&auto=format&fit=crop" },
  { name: "Documentos", url: "https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=1200&auto=format&fit=crop" },
];

// Rich Template for Calculators Module Post
const CALCULATORS_POST_TEMPLATE = {
  title: "🚀 ¡Presentamos el Nuevo Módulo de Calculadoras Jurídicas Especializadas!",
  category: "Nuevas Funciones",
  readTime: "4 min",
  imageUrl: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop",
  excerpt: "Revoluciona tu práctica legal con nuestras calculadoras automáticas de liquidación laboral, perjuicios civiles, fórmulas DTF+mora administrativas, dosimetría penal y términos procesales.",
  content: `# 🚀 ¡Presentamos el Nuevo Módulo de Calculadoras Jurídicas Especializadas!

Nos complace anunciar el lanzamiento oficial de nuestra suite de **Calculadoras Jurídicas Automáticas**, una herramienta diseñada para transformar la manera en que realizas liquidaciones de derechos laborales, cuantificación de perjuicios civiles, fórmulas administrativas con DTF, dosimetría penal y cómputo de términos procesales.

---

[Demonstración en Vídeo: Cómo usar el Módulo de Calculadoras Jurídicas](https://www.youtube.com/watch?v=dQw4w9WgXcQ)

---

## 💡 ¿Por qué utilizar el Módulo de Calculadoras?

> [!TIP]
> **Ahorro de Tiempo Garantizado**: Realiza en segundos liquidaciones complejas que antes requerían horas de hojas de cálculo y fórmulas manuales. Todas las tasas y valores del IPC se actualizan automáticamente en tiempo real.

![Interfaz de Calculadoras Jurídicas](https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop)

---

## ⚖️ Funciones Destacadas por Área del Derecho

### 1. 💼 Área Laboral y Seguridad Social
- **Liquidación de Contrato Laboral**: Cómputo preciso de Cesantías, Intereses sobre Cesantías, Prima de Servicios y Vacaciones proporcionales.
- **Indemnización por Despido Sin Justa Causa**: Aplicación automática de los parámetros del *Art. 64 del Código Sustantivo del Trabajo* para contratos a término fijo e indefinido.
- **Recargos y Horas Extra**: Liquidación de recargo nocturno (35%), dominical/festivo (75%), horas extra diurnas/nocturnas con recargos legales.

### 2. 📜 Área Civil y Comercial
- **Liquidación de Perjuicios Materiales**: Daño emergente y lucro cesante consolidado y futuro con tasa de descuento legal.
- **Actualización con IPC (Indexación)**: Cálculo oficial de pérdida de poder adquisitivo según la serie de variación del IPC del DANE.
- **Intereses Moratorios Comerciales**: Tasa máxima de usura certificada por la *Superintendencia Financiera de Colombia*.
- **Cuota Alimentaria**: Actualización de cuotas alimentarias fijadas en salarios mínimos o IPC.

### 3. 🏛️ Sentencias Administrativas (Consejo de Estado)
- **Liquidación de Sentencias de Reparación Directa**: Fórmula doble autorizada (*Fase 1: Tasa DTF + Fase 2: Mora Comercial*).
- **Conversión de Tasas Efectivas**: Conversión id = (1+TEA)^(1/365)-1 prohibiendo el anatocismo legal.

### 4. ⚖️ Dosimetría Penal y Ejecución de Penas
- **Sistema de Cuartos Penales**: Determinación exacta del cuarto mínimo, cuartos medios y cuarto máximo a partir de penas mínimas y máximas.
- **Redención de Pena**: Cálculo de días redimidos por estudio (2x1) y trabajo según legislación penitenciaria.

### 5. ⏳ Términos Procesales y Caducidad
- **Caducidad de Acciones**: Cómputo exacto de caducidad en reparación directa, nulidad y restablecimiento del derecho.
- **Cómputo de Días Hábiles**: Detección automática de fines de semana y vacancias judiciales.

---

## 📊 Comparativa: Método Tradicional vs. Calculadoras Legal AI

| Característica / Función | Método Tradicional (Excel Manual) | Nueva Suite Legal AI |
| :--- | :--- | :--- |
| **Tiempo Promedio de Cálculo** | 45 a 60 minutos por caso | **Menos de 3 segundos** ⚡ |
| **Actualización de Tasas e IPC** | Búsqueda manual en DANE / Superfinanciera | **Integración en tiempo real en la nube** |
| **Riesgo de Error Humano** | Alto (fórmulas mal aplicadas) | **0% Error algorítmico garantizado** |
| **Informe de Resultados** | Texto plano o tabla suelta | **Reporte PDF / Excel Oficial listo para juzgado** |

---

> [!IMPORTANT]
> **¿Cómo acceder a las Calculadoras?**
> Puedes ingresar directamente desde el menú lateral haciendo clic en **Calculadoras** o navegando a [/dashboard/calculadoras](/dashboard/calculadoras).

---

> [!NOTE]
> **Soporte y Comentarios**: Si requieres una calculadora adicional para tu área de práctica, contáctanos a través del canal de soporte en la plataforma.`
};

// Rich Template for Marketing & Lead Gen Captation
const MARKETING_LEAD_GEN_TEMPLATE = {
  title: "🚀 Automatiza tu Práctica Jurídica con Mi Asistente Legal IA",
  category: "Avisos",
  readTime: "3 min",
  imageUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop",
  excerpt: "Descubre cómo cientos de abogados y firmas reducen más del 70% del tiempo de gestión de expedientes, liquidación de términos e investigación legal.",
  content: `# 🚀 Automatiza tu Práctica Jurídica con Mi Asistente Legal IA

¿Sabías que los abogados y despachos que integran **Inteligencia Artificial en su práctica diaria** reducen hasta un 70% el tiempo dedicado a tareas repetitivas de investigación y redacción?

Te invitamos a descubrir **Mi Asistente Legal IA**, la plataforma diseñada específicamente para transformar el ejercicio del derecho en Colombia.

---

## ⚡ ¿Por qué elegir Mi Asistente Legal IA?

> [!TIP]
> **Prueba Gratuita Sin Compromiso**: Regístrate hoy y accede de inmediato a nuestras calculadoras especializadas, consultas de procesos judiciales en tiempo real y asistente de redacción con IA.

![Plataforma Mi Asistente Legal IA](https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop)

---

### 📊 Comparativa de Rendimiento

| Módulo de la Plataforma | Gestión Tradicional | Con Mi Asistente Legal IA |
| :--- | :--- | :--- |
| **Búsqueda de Procesos Judiciales** | 10 - 15 min por expediente | **Menos de 1 segundo** ⚡ |
| **Liquidación de Contratos y Perjuicios** | Excel manual propenso a error | **Calculadoras automáticas con IPC real** |
| **Monitoreo de Actualizaciones** | Revisión manual diaria | **Notificaciones e historial automático** 🔔 |

---

## 🎁 Beneficios Exclusivos al Registrarte Hoy

1. **Calculadoras Jurídicas Automáticas**: Laboral, indemnizaciones, IPC, dosimetría penal y caducidad.
2. **Seguimiento Inteligente de Procesos**: Monitoreo dinámico con estado y nivel de riesgo.
3. **Reportes PDF Oficiales**: Exporta informes formateados listos para presentar ante juzgados o clientes.

---

> [!IMPORTANT]
> **¡Comienza tu Prueba Gratuita Ahora!**
> Haz clic en el enlace a continuación para crear tu cuenta en menos de 1 minuto y potenciar tu despacho:
>
> [🚀 Registrarme Gratis en Mi Asistente Legal IA](https://app.miasistentelegalia.com/register)`
};

// Rich Template for Bulk Ingestion Post
const BULK_INGESTION_POST_TEMPLATE = {
  title: "🚀 ¡Nueva Ingesta Masiva de Procesos Judiciales: Carga hasta 1,000 Expedientes en Segundos!",
  category: "Nuevas Funciones",
  readTime: "3 min",
  imageUrl: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop",
  excerpt: "Carga masivamente hasta 1,000 radicados por archivo Excel (.xlsx) o CSV (.csv) con validación automática y soporte multi-origen (SAMAI, CPNU, TYBA y OTRO).",
  content: `# 🚀 ¡Nueva Ingesta Masiva de Procesos Judiciales: Carga hasta 1,000 Expedientes en Segundos!

Nos complace anunciar el lanzamiento de la **Ingesta Masiva de Procesos Judiciales**, una potente herramienta diseñada para despachos, firmas de abogados y departamentos jurídicos que gestionan grandes volúmenes de expedientes en Colombia.

Ahora puedes importar masivamente hasta **1,000 procesos por archivo** en formato **Excel (.xlsx)** o **CSV (.csv)** sin necesidad de ingresar cada número de radicado uno por uno.

---

## 💡 ¿Por qué utilizar la Ingesta Masiva?

> [!TIP]
> **Productividad Exponencial**: Automatiza en un solo clic la carga inicial de tu inventario de procesos. El sistema detecta y estandariza los orígenes de las fuentes oficiales (SAMAI, CPNU, TYBA u OTRO) y valida los 23 dígitos del radicado automáticamente.

![Modal de Ingesta Masiva de Procesos Judiciales](https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop)

---

## ⚙️ Paso a Paso: Cómo Realizar una Ingesta Masiva

### 1. 📥 Descargar la Plantilla Pre-formateada
Desde el modal de Ingesta Masiva puedes descargar la plantilla oficial en **Excel (.xlsx)** o **CSV (.csv)** con las columnas requeridas:
- \`radicado\`: Número único de 23 dígitos de la actuación judicial.
- \`origen\`: Fuente o portal de consulta (\`SAMAI\`, \`CPNU\`, \`TYBA\` u \`OTRO\`).

### 2. 📄 Cargar tu Archivo (Arrastrar y Soltar)
Arrastra tu archivo compilado directamente en la zona de carga. La plataforma soporta hasta **1,000 registros por archivo** codificados en UTF-8.

### 3. ⚡ Procesamiento Asíncrono e Historial
Una vez iniciada la ingesta, nuestro motor procesa las solicitudes en segundo plano sin interrumpir tu trabajo diario en el dashboard.

---

## 📊 Comparativa: Carga Manual vs. Ingesta Masiva Legal AI

| Característica / Función | Método Manual (Uno a Uno) | Nueva Ingesta Masiva (.xlsx / .csv) |
| :--- | :--- | :--- |
| **Capacidad de Carga** | 1 proceso por formulario | **Hasta 1,000 procesos por lote** ⚡ |
| **Tiempo de Registro** | 2 a 3 horas para 100 procesos | **Menos de 5 segundos** |
| **Validación de Formato** | Manual sujeto a tipografía | **Detección automática de 23 dígitos** |
| **Soporte de Orígenes** | Selección manual por ítem | **Mapeo automático (SAMAI, CPNU, TYBA, OTRO)** |

---

> [!IMPORTANT]
> **¿Dónde encuentro esta opción?**
> Ingresa al módulo de **Despachos / Consulta de Procesos** y haz clic en el botón con el icono de carga **"Ingesta Masiva"** en la esquina superior derecha.

---

> [!NOTE]
> **Soporte de Archivos**: Recuerda que puedes utilizar archivos \`.xlsx\` estándar de Microsoft Excel o archivos de texto plano \`.csv\`. Si necesitas asistencia con la migración masiva de tus expedientes, contáctanos a través de nuestro equipo de soporte.`
};

export default function BulletinsPage() {
  const { profile, loading: authLoading } = useAuth();
  const apiClient = useApiClient();
  const isAdmin = profile?.role === "admin" || profile?.role === "contributor";

  const [bulletins, setBulletins] = useState<Bulletin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  
  // Modals / Details
  const [activeBulletin, setActiveBulletin] = useState<Bulletin | null>(null);
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  
  // Editor State
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<"create" | "edit">("create");
  const [editorId, setEditorId] = useState<string | null>(null);
  
  // Form Fields
  const [formTitle, setFormTitle] = useState("");
  const [formCategory, setFormCategory] = useState("Nuevas Funciones");
  const [formImageUrl, setFormImageUrl] = useState("");
  const [formExcerpt, setFormExcerpt] = useState("");
  const [formReadTime, setFormReadTime] = useState("3 min");
  const [formAuthorName, setFormAuthorName] = useState("Equipo Legal AI");
  const [formStatus, setFormStatus] = useState<"draft" | "published">("published");
  const [formContent, setFormContent] = useState("");
  const [formSendEmail, setFormSendEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorLayout, setEditorLayout] = useState<"edit" | "split" | "preview">("split");
  const [isCoverGalleryOpen, setIsCoverGalleryOpen] = useState(false);
  // Marketing & Analytics Modals State
  const [isMarketingModalOpen, setIsMarketingModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [analyticsBulletinId, setAnalyticsBulletinId] = useState<string | null>(null);
  const [analyticsBulletinTitle, setAnalyticsBulletinTitle] = useState("");
  const [formTargetAudience, setFormTargetAudience] = useState("all");
  const [formCustomEmails, setFormCustomEmails] = useState("");

  // Direct Broadcast Campaign Modal State
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [broadcastBulletin, setBroadcastBulletin] = useState<Bulletin | null>(null);
  const [broadcastAudience, setBroadcastAudience] = useState<"marketing_leads" | "users" | "all" | "custom">("marketing_leads");
  const [broadcastCustomEmails, setBroadcastCustomEmails] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  const handleOpenBroadcastModal = (bulletin: Bulletin) => {
    setBroadcastBulletin(bulletin);
    setBroadcastAudience("marketing_leads");
    setBroadcastCustomEmails("");
    setIsBroadcastModalOpen(true);
  };

  const handleExecuteBroadcast = async () => {
    if (!broadcastBulletin) return;
    setBroadcasting(true);

    const parsedCustomEmails = broadcastAudience === "custom"
      ? broadcastCustomEmails.split(/[,;\n]/).map((e) => e.trim()).filter((e) => e.includes("@"))
      : [];

    if (broadcastAudience === "custom" && parsedCustomEmails.length === 0) {
      toast.warning("Ingresa al menos un correo electrónico válido.");
      setBroadcasting(false);
      return;
    }

    try {
      const res = await apiClient.post(`/api/v1/bulletins/${broadcastBulletin.id}/broadcast`, {
        target_audience: broadcastAudience,
        custom_emails: parsedCustomEmails,
      });

      if (res.ok) {
        toast.success(res.data?.message || "Campaña de correo iniciada en segundo plano.");
        setIsBroadcastModalOpen(false);
      } else {
        toast.error(res.data?.detail || "No se pudo iniciar el envío de correos.");
      }
    } catch (err: any) {
      console.error("Error launching broadcast:", err);
      toast.error("Error de conexión al servidor.");
    } finally {
      setBroadcasting(false);
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Handle direct image paste (Ctrl+V) into Markdown Textarea
  const handlePasteImage = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // 1. Check for File objects in clipboardData.files or items
    let imageFile: File | null = null;

    if (clipboardData.files && clipboardData.files.length > 0) {
      for (let i = 0; i < clipboardData.files.length; i++) {
        if (clipboardData.files[i].type.startsWith("image/")) {
          imageFile = clipboardData.files[i];
          break;
        }
      }
    }

    if (!imageFile && clipboardData.items) {
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.type.startsWith("image/") || (item.kind === "file" && item.type.includes("image"))) {
          imageFile = item.getAsFile();
          if (imageFile) break;
        }
      }
    }

    if (imageFile) {
      e.preventDefault();
      const fileToUpload = imageFile;
      
      const formData = new FormData();
      formData.append("file", fileToUpload);

      toast.info("📷 Subiendo imagen capturada al servidor...");

      apiClient.post("/api/v1/bulletins/upload-image", formData).then((res) => {
        if (res.ok && res.data?.url) {
          const uploadedUrl = res.data.url;
          const textarea = textareaRef.current;
          setFormContent((prevContent) => {
            const start = textarea?.selectionStart ?? prevContent.length;
            const end = textarea?.selectionEnd ?? prevContent.length;
            const imageMarkdown = `\n![Imagen Pegada](${uploadedUrl})\n`;
            return prevContent.substring(0, start) + imageMarkdown + prevContent.substring(end);
          });
          toast.success("📷 ¡Imagen subida y alojada correctamente en la nube!");
        } else {
          // Fallback to Base64 if upload endpoint fails
          const reader = new FileReader();
          reader.onload = (event) => {
            const base64Data = event.target?.result as string;
            if (!base64Data) return;
            const textarea = textareaRef.current;
            setFormContent((prevContent) => {
              const start = textarea?.selectionStart ?? prevContent.length;
              const end = textarea?.selectionEnd ?? prevContent.length;
              const imageMarkdown = `\n![Imagen Pegada](${base64Data})\n`;
              return prevContent.substring(0, start) + imageMarkdown + prevContent.substring(end);
            });
            toast.success("📷 Imagen insertada en el editor.");
          };
          reader.readAsDataURL(fileToUpload);
        }
      }).catch((err) => {
        console.error("Upload error on paste:", err);
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64Data = event.target?.result as string;
          if (!base64Data) return;
          const textarea = textareaRef.current;
          setFormContent((prevContent) => {
            const start = textarea?.selectionStart ?? prevContent.length;
            const end = textarea?.selectionEnd ?? prevContent.length;
            const imageMarkdown = `\n![Imagen Pegada](${base64Data})\n`;
            return prevContent.substring(0, start) + imageMarkdown + prevContent.substring(end);
          });
        };
        reader.readAsDataURL(fileToUpload);
      });

      return;
    }

    // 2. Check for HTML content containing <img src="..."> copied from web/Word
    const htmlData = clipboardData.getData("text/html");
    if (htmlData && htmlData.includes("<img")) {
      const match = htmlData.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (match && match[1]) {
        e.preventDefault();
        const imgSrc = match[1];
        const textarea = textareaRef.current;
        setFormContent((prevContent) => {
          const start = textarea?.selectionStart ?? prevContent.length;
          const end = textarea?.selectionEnd ?? prevContent.length;
          const imageMarkdown = `\n![Imagen Copiada](${imgSrc})\n`;
          return prevContent.substring(0, start) + imageMarkdown + prevContent.substring(end);
        });
        toast.success("📷 Imagen de página web/documento pegada correctamente.");
      }
    }
  };

  // Handle direct file drag & drop into Markdown Textarea
  const handleDropImage = (e: React.DragEvent<HTMLTextAreaElement>) => {
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.type.startsWith("image/")) {
      e.preventDefault();
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64Data = event.target?.result as string;
        if (!base64Data) return;

        const textarea = textareaRef.current;
        setFormContent((prevContent) => {
          const start = textarea?.selectionStart ?? prevContent.length;
          const end = textarea?.selectionEnd ?? prevContent.length;
          const imageMarkdown = `\n![${file.name}](${base64Data})\n`;
          return prevContent.substring(0, start) + imageMarkdown + prevContent.substring(end);
        });
        toast.success(`📷 Imagen '${file.name}' insertada correctamente.`);
      };
      reader.readAsDataURL(file);
    }
  };

  // Auto-calculate word count & read time
  const wordCount = formContent.trim() ? formContent.trim().split(/\s+/).length : 0;
  const charCount = formContent.length;
  
  useEffect(() => {
    if (wordCount > 0) {
      const minutes = Math.max(1, Math.ceil(wordCount / 200));
      setFormReadTime(`${minutes} min`);
    }
  }, [wordCount]);

  // Fetch bulletins
  const fetchBulletins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/v1/bulletins");
      if (res.ok) {
        setBulletins(res.data.bulletins || []);
      } else {
        toast.error("No se pudieron cargar los boletines.");
      }
    } catch (err) {
      console.error("Error loading bulletins:", err);
      toast.error("Error de conexión al cargar boletines.");
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchBulletins();
  }, [fetchBulletins]);

  if (authLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
        <Loader2 className="h-10 w-10 animate-spin text-pink-500" />
        <span className="ml-3 text-slate-500 dark:text-slate-400 font-medium">Cargando perfil...</span>
      </div>
    );
  }

  if (profile?.role !== "admin" && profile?.role !== "contributor") {
    return <Navigate to="/dashboard" replace />;
  }

  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlSlug = slug || searchParams.get("slug");

  // Auto-open reader if URL contains a slug
  useEffect(() => {
    if (urlSlug && bulletins.length > 0) {
      const match = bulletins.find((b) => b.slug === urlSlug || b.id === urlSlug);
      if (match) {
        setActiveBulletin(match);
        setIsReaderOpen(true);
      }
    }
  }, [urlSlug, bulletins]);

  // Open bulletin in reader and update URL bar
  const handleOpenReader = (bulletin: Bulletin) => {
    setActiveBulletin(bulletin);
    setIsReaderOpen(true);
    if (bulletin.slug) {
      navigate(`/dashboard/boletines/${bulletin.slug}`, { replace: false });
    }
  };

  const handleCloseReader = () => {
    setIsReaderOpen(false);
    navigate("/dashboard/boletines", { replace: false });
  };

  // Open editor for creating
  const handleOpenCreate = () => {
    setEditorMode("create");
    setEditorId(null);
    setFormTitle("");
    setFormCategory("Nuevas Funciones");
    setFormImageUrl("");
    setFormExcerpt("");
    setFormReadTime("3 min");
    setFormAuthorName("Equipo Legal AI");
    setFormStatus("published");
    setFormContent("");
    setFormSendEmail(false);
    setEditorLayout("split");
    setIsEditorOpen(true);
  };

  // Quick prefill Calculators post
  const handlePrefillCalculatorsPost = () => {
    setEditorMode("create");
    setEditorId(null);
    setFormTitle(CALCULATORS_POST_TEMPLATE.title);
    setFormCategory(CALCULATORS_POST_TEMPLATE.category);
    setFormImageUrl(CALCULATORS_POST_TEMPLATE.imageUrl);
    setFormExcerpt(CALCULATORS_POST_TEMPLATE.excerpt);
    setFormReadTime(CALCULATORS_POST_TEMPLATE.readTime);
    setFormAuthorName("Equipo Legal AI");
    setFormStatus("published");
    setFormContent(CALCULATORS_POST_TEMPLATE.content);
    setFormSendEmail(true);
    setEditorLayout("split");
    setIsEditorOpen(true);
    toast.success("Plantilla 'Nuevas Funciones: Calculadoras Jurídicas' cargada en el editor.");
  };

  // Quick prefill Marketing & Leads post
  const handlePrefillMarketingPost = () => {
    setEditorMode("create");
    setEditorId(null);
    setFormTitle(MARKETING_LEAD_GEN_TEMPLATE.title);
    setFormCategory(MARKETING_LEAD_GEN_TEMPLATE.category);
    setFormImageUrl(MARKETING_LEAD_GEN_TEMPLATE.imageUrl);
    setFormExcerpt(MARKETING_LEAD_GEN_TEMPLATE.excerpt);
    setFormReadTime(MARKETING_LEAD_GEN_TEMPLATE.readTime);
    setFormAuthorName("Equipo Legal AI");
    setFormStatus("published");
    setFormContent(MARKETING_LEAD_GEN_TEMPLATE.content);
    setFormTargetAudience("marketing_leads");
    setFormSendEmail(true);
    setEditorLayout("split");
    setIsEditorOpen(true);
    toast.success("Plantilla 'Marketing y Captación de Leads' cargada en el editor.");
  };

  // Quick prefill Bulk Ingestion post
  const handlePrefillBulkIngestionPost = () => {
    setEditorMode("create");
    setEditorId(null);
    setFormTitle(BULK_INGESTION_POST_TEMPLATE.title);
    setFormCategory(BULK_INGESTION_POST_TEMPLATE.category);
    setFormImageUrl(BULK_INGESTION_POST_TEMPLATE.imageUrl);
    setFormExcerpt(BULK_INGESTION_POST_TEMPLATE.excerpt);
    setFormReadTime(BULK_INGESTION_POST_TEMPLATE.readTime);
    setFormAuthorName("Equipo Legal AI");
    setFormStatus("published");
    setFormContent(BULK_INGESTION_POST_TEMPLATE.content);
    setFormTargetAudience("all");
    setFormSendEmail(true);
    setEditorLayout("split");
    setIsEditorOpen(true);
    toast.success("Plantilla 'Nuevas Funciones: Ingesta Masiva' cargada en el editor.");
  };

  // Apply templates inside toolbar
  const handleApplyTemplate = (templateType: "calculadoras" | "release" | "tutorial" | "marketing" | "ingesta_masiva") => {
    if (templateType === "calculadoras") {
      setFormTitle(CALCULATORS_POST_TEMPLATE.title);
      setFormCategory(CALCULATORS_POST_TEMPLATE.category);
      setFormImageUrl(CALCULATORS_POST_TEMPLATE.imageUrl);
      setFormExcerpt(CALCULATORS_POST_TEMPLATE.excerpt);
      setFormContent(CALCULATORS_POST_TEMPLATE.content);
      toast.success("Plantilla de Calculadoras aplicada.");
    } else if (templateType === "ingesta_masiva") {
      setFormTitle(BULK_INGESTION_POST_TEMPLATE.title);
      setFormCategory(BULK_INGESTION_POST_TEMPLATE.category);
      setFormImageUrl(BULK_INGESTION_POST_TEMPLATE.imageUrl);
      setFormExcerpt(BULK_INGESTION_POST_TEMPLATE.excerpt);
      setFormContent(BULK_INGESTION_POST_TEMPLATE.content);
      toast.success("Plantilla de Ingesta Masiva aplicada.");
    } else if (templateType === "marketing") {
      setFormTitle(MARKETING_LEAD_GEN_TEMPLATE.title);
      setFormCategory(MARKETING_LEAD_GEN_TEMPLATE.category);
      setFormImageUrl(MARKETING_LEAD_GEN_TEMPLATE.imageUrl);
      setFormExcerpt(MARKETING_LEAD_GEN_TEMPLATE.excerpt);
      setFormContent(MARKETING_LEAD_GEN_TEMPLATE.content);
      setFormTargetAudience("marketing_leads");
      toast.success("Plantilla de Marketing & Leads aplicada.");
    } else if (templateType === "release") {
      setFormTitle("⚡ Release Notes v2.4: Mejoras de Rendimiento y Sistema");
      setFormCategory("Actualizaciones");
      setFormContent(`# ⚡ Release Notes v2.4\n\nNos complace presentar la versión **v2.4** de la plataforma.\n\n## ✨ Nuevas Mejoras\n- **Optimización de consultas**: Reducción del tiempo de respuesta en un 40%.\n- **Soporte de Medios Enriquecidos**: Editor profesional con soporte de vídeos e imágenes.\n\n> [!NOTE]\n> Todos los cambios ya se encuentran activos en producción.`);
      toast.success("Plantilla Release Notes aplicada.");
    } else if (templateType === "tutorial") {
      setFormTitle("📚 Tutorial: Guía Paso a Paso para la Plataforma");
      setFormCategory("Tutoriales");
      setFormContent(`# 📚 Guía y Tutorial Paso a Paso\n\nAprende a aprovechar al máximo todas las funciones en 3 sencillos pasos.\n\n[Ver Vídeo Tutorial Explicativo](https://www.youtube.com/watch?v=dQw4w9WgXcQ)\n\n### Paso 1: Configura tu Perfil\nIngresa al panel de ajustes...\n\n> [!TIP]\n> Guarda tus búsquedas frecuentes para recibir alertas en tiempo real.`);
      toast.success("Plantilla de Tutorial aplicada.");
    }
  };

  // Open editor for editing
  const handleOpenEdit = (bulletin: Bulletin) => {
    setEditorMode("edit");
    setEditorId(bulletin.id);
    setFormTitle(bulletin.title);
    setFormCategory(bulletin.category);
    setFormImageUrl(bulletin.image_url || "");
    setFormExcerpt(bulletin.excerpt || "");
    setFormReadTime(bulletin.read_time);
    setFormAuthorName(bulletin.author_name);
    setFormStatus(bulletin.status);
    setFormContent(bulletin.content);
    setFormSendEmail(false);
    setEditorLayout("split");
    setIsEditorOpen(true);
  };

  // Save bulletin
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle || !formContent) {
      toast.warning("El título y el contenido son campos obligatorios.");
      return;
    }

    const parsedCustomEmails = formTargetAudience === "custom"
      ? formCustomEmails.split(/[,;\n]/).map((e) => e.trim()).filter((e) => e.includes("@"))
      : [];

    const payload = {
      title: formTitle,
      category: formCategory,
      content: formContent,
      excerpt: formExcerpt || undefined,
      image_url: formImageUrl || undefined,
      read_time: formReadTime,
      author_name: formAuthorName,
      status: formStatus,
      send_email: formSendEmail,
      target_audience: formTargetAudience,
      custom_emails: parsedCustomEmails
    };

    try {
      let res;
      if (editorMode === "create") {
        res = await apiClient.post("/api/v1/bulletins", payload);
      } else {
        res = await apiClient.put(`/api/v1/bulletins/${editorId}`, payload);
      }

      if (res.ok) {
        toast.success(
          editorMode === "create" 
            ? "Boletín publicado con éxito." 
            : "Boletín actualizado con éxito."
        );
        if (formSendEmail && formStatus === "published") {
          toast.info("Enviando distribución de correos a usuarios en segundo plano...");
        }
        setIsEditorOpen(false);
        fetchBulletins();
      } else {
        toast.error(res.data?.detail || "No se pudo guardar el boletín.");
      }
    } catch (err) {
      console.error("Error saving bulletin:", err);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  // Delete bulletin
  const [bulletinToDelete, setBulletinToDelete] = useState<Bulletin | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = (bulletin: Bulletin) => {
    setBulletinToDelete(bulletin);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!bulletinToDelete) return;
    setDeleting(true);
    try {
      const res = await apiClient.delete(`/api/v1/bulletins/${bulletinToDelete.id}`);
      if (res.ok) {
        toast.success("Boletín eliminado correctamente.");
        setIsDeleteOpen(false);
        fetchBulletins();
      } else {
        toast.error("No se pudo eliminar el boletín.");
      }
    } catch (err) {
      console.error("Error deleting bulletin:", err);
      toast.error("Error al intentar eliminar.");
    } finally {
      setDeleting(false);
      setBulletinToDelete(null);
    }
  };

  // Filter bulletins for display
  const filteredBulletins = bulletins.filter((b) => {
    const matchesSearch = 
      b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.excerpt && b.excerpt.toLowerCase().includes(searchQuery.toLowerCase()));
      
    const matchesCategory = 
      selectedCategory === "Todos" || b.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const getCategoryBadgeClass = (category: string) => {
    switch (category) {
      case "Actualizaciones":
        return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400";
      case "Nuevas Funciones":
        return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400";
      case "Tutoriales":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400";
      case "Avisos":
        return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400";
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#f8fafc] dark:bg-slate-950 min-h-screen">
      {/* Premium Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 p-8 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-md shadow-pink-200 dark:shadow-none">
                <Newspaper className="h-5.5 w-5.5 text-white" />
              </div>
              <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                Boletines y Novedades
              </h1>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-md">
              Editor profesional de boletines con imágenes, vídeos y texto enriquecido.
            </p>
          </div>
          
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => setIsMarketingModalOpen(true)}
                variant="outline"
                className="border-purple-500/40 bg-purple-50/50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 font-bold py-6 px-5 rounded-2xl flex items-center gap-2"
              >
                <Users className="h-4.5 w-4.5 text-purple-600 dark:text-purple-400" />
                Lista de Difusión
              </Button>

              <Button
                onClick={handlePrefillCalculatorsPost}
                variant="outline"
                className="border-pink-500/30 text-pink-600 dark:text-pink-400 hover:bg-pink-50 dark:hover:bg-pink-950/40 font-bold py-6 px-5 rounded-2xl flex items-center gap-2"
              >
                <Sparkles className="h-4.5 w-4.5 text-pink-500" />
                Post: Calculadoras
              </Button>

              <Button
                onClick={handlePrefillMarketingPost}
                variant="outline"
                className="border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40 font-bold py-6 px-5 rounded-2xl flex items-center gap-2"
              >
                <Target className="h-4.5 w-4.5 text-purple-500" />
                Post: Captación Leads
              </Button>

              <Button
                onClick={handlePrefillBulkIngestionPost}
                variant="outline"
                className="border-indigo-500/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 font-bold py-6 px-5 rounded-2xl flex items-center gap-2"
              >
                <Upload className="h-4.5 w-4.5 text-indigo-500" />
                Post: Ingesta Masiva
              </Button>

              <Button
                onClick={handleOpenCreate}
                className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold py-6 px-6 rounded-2xl shadow-lg shadow-pink-100 dark:shadow-none transition-all hover:scale-102 flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Nuevo Boletín
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full p-6 sm:p-8 space-y-6 flex-1 flex flex-col">
        {isAdmin ? (
          <Tabs defaultValue="feed" className="w-full space-y-6">
            <div className="flex justify-between items-center border-b border-slate-200 dark:border-slate-800 pb-3">
              <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 rounded-xl shadow-inner gap-1">
                <TabsTrigger 
                  value="feed" 
                  className="rounded-lg px-5 py-2 text-sm font-semibold transition-all data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver como Usuario
                </TabsTrigger>
                <TabsTrigger 
                  value="admin" 
                  className="rounded-lg px-5 py-2 text-sm font-semibold transition-all data-[state=active]:bg-white dark:data-[state=active]:bg-slate-950 data-[state=active]:text-slate-900 dark:data-[state=active]:text-white data-[state=active]:shadow-sm"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Administrador
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="feed" className="mt-0 outline-none space-y-6">
              {renderFeedSection()}
            </TabsContent>

            <TabsContent value="admin" className="mt-0 outline-none space-y-6">
              {renderAdminSection()}
            </TabsContent>
          </Tabs>
        ) : (
          renderFeedSection()
        )}
      </div>

      {/* Reader Modal View with Rich Media Renderer */}
      <Dialog open={isReaderOpen} onOpenChange={(open) => { if (!open) handleCloseReader(); else setIsReaderOpen(true); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-[2rem] border-none shadow-2xl p-0 bg-white dark:bg-slate-900">
          {activeBulletin && (
            <div className="flex flex-col">
              {/* Cover Image / Gradient */}
              <div className="relative h-72 sm:h-96 w-full overflow-hidden">
                <img 
                  src={activeBulletin.image_url || DEFAULT_COVER} 
                  alt={activeBulletin.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                
                {/* Close Button */}
                <button
                  onClick={() => setIsReaderOpen(false)}
                  className="absolute top-4 right-4 z-20 bg-black/40 hover:bg-black/70 text-white rounded-full p-2.5 backdrop-blur-md transition-all"
                >
                  <X className="w-5 h-5" />
                </button>

                {/* Image Overlay Header */}
                <div className="absolute bottom-8 left-8 right-8 text-white space-y-3">
                  <div className="flex items-center gap-3">
                    <Badge className={cn("border-none px-3 py-1 font-extrabold uppercase text-xs tracking-wider", getCategoryBadgeClass(activeBulletin.category))}>
                      {activeBulletin.category}
                    </Badge>
                    <span className="text-xs text-slate-300 font-semibold bg-white/10 px-3 py-1 rounded-full backdrop-blur-md border border-white/10">
                      {activeBulletin.read_time} de lectura
                    </span>
                  </div>
                  <h2 className="text-2xl sm:text-4xl font-black leading-tight tracking-tight drop-shadow-md">
                    {activeBulletin.title}
                  </h2>
                </div>
              </div>

              {/* Meta information */}
              <div className="px-8 py-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex flex-wrap gap-4 items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-pink-500 text-white flex items-center justify-center font-bold text-xs shadow-sm">
                    {activeBulletin.author_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <span className="font-bold text-slate-800 dark:text-slate-200 block">
                      {activeBulletin.author_name}
                    </span>
                    <span className="text-[11px] text-slate-400">Autor Oficial</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-pink-500" />
                    <span className="font-semibold text-slate-600 dark:text-slate-300">
                      {activeBulletin.published_at 
                        ? new Date(activeBulletin.published_at).toLocaleDateString("es-ES", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                          })
                        : new Date(activeBulletin.created_at).toLocaleDateString("es-ES")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => {
                        const shareUrl = activeBulletin?.slug 
                          ? `${window.location.origin}/dashboard/boletines/${activeBulletin.slug}`
                          : window.location.href;
                        navigator.clipboard.writeText(shareUrl);
                        toast.success("Enlace directo del boletín copiado");
                      }}
                      className="rounded-xl h-8 text-xs font-bold gap-1"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Compartir
                    </Button>
                  </div>
                </div>
              </div>

              {/* Rich Content Body Renderer */}
              <div className="p-8 sm:p-10 bg-white dark:bg-slate-900">
                {activeBulletin.excerpt && (
                  <p className="text-slate-600 dark:text-slate-300 text-lg font-medium leading-relaxed italic border-l-4 border-pink-500 pl-4 py-1 mb-8 bg-pink-50/50 dark:bg-pink-950/20 rounded-r-2xl">
                    {activeBulletin.excerpt}
                  </p>
                )}
                
                <RichBulletinRenderer content={activeBulletin.content} />
              </div>

              <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center">
                <span className="text-xs text-slate-400 font-medium">
                  Mi Asistente Legal IA • Sistema de Boletines
                </span>
                <Button 
                  variant="outline" 
                  onClick={handleCloseReader}
                  className="rounded-xl px-6 font-bold border-slate-200 dark:border-slate-700"
                >
                  Cerrar Lectura
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Editor Modal with Rich Toolbar */}
      <Dialog open={isEditorOpen} onOpenChange={(open) => !saving && setIsEditorOpen(open)}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto rounded-[2rem] border-none shadow-2xl p-0 bg-white dark:bg-slate-900">
          <form onSubmit={handleSave} className="flex flex-col">
            <DialogHeader className="p-8 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-t-[2rem]">
              <div className="flex justify-between items-center">
                <div>
                  <DialogTitle className="text-2xl font-black">
                    {editorMode === "create" ? "Crear Boletín Profesional" : "Editar Boletín"}
                  </DialogTitle>
                  <DialogDescription className="text-pink-100 mt-1">
                    Diseña noticias y avisos con imágenes, vídeos interactivos y bloques enriquecidos.
                  </DialogDescription>
                </div>

                <Badge className="bg-white/20 text-white border-none px-3 py-1 text-xs font-bold backdrop-blur-md">
                  Editor Profesional v2.0
                </Badge>
              </div>
            </DialogHeader>

            <div className="p-8 space-y-6">
              {/* Grid 2 Columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Título del Boletín *</label>
                  <Input 
                    required
                    placeholder="Ej. 🚀 ¡Presentamos el Nuevo Módulo de Calculadoras!"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 font-bold"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Categoría</label>
                  <Select value={formCategory} onValueChange={setFormCategory}>
                    <SelectTrigger className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 font-semibold">
                      <SelectValue placeholder="Selecciona una categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">URL Imagen de Portada</label>
                    <button
                      type="button"
                      onClick={() => setIsCoverGalleryOpen(true)}
                      className="text-xs text-pink-600 dark:text-pink-400 font-bold hover:underline flex items-center gap-1"
                    >
                      <ImageIcon className="w-3.5 h-3.5" /> Galería de Portadas
                    </button>
                  </div>
                  <Input 
                    placeholder="Ej. https://images.unsplash.com/... (vacío para imagen por defecto)"
                    value={formImageUrl}
                    onChange={(e) => setFormImageUrl(e.target.value)}
                    className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-xs"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tiempo Lectura</label>
                    <Input 
                      placeholder="Ej. 3 min"
                      value={formReadTime}
                      onChange={(e) => setFormReadTime(e.target.value)}
                      className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Autor</label>
                    <Input 
                      placeholder="Ej. Equipo Legal AI"
                      value={formAuthorName}
                      onChange={(e) => setFormAuthorName(e.target.value)}
                      className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Resumen / Extracto</label>
                <Input 
                  placeholder="Una breve descripción destacada para la tarjeta de noticias..."
                  value={formExcerpt}
                  onChange={(e) => setFormExcerpt(e.target.value)}
                  className="rounded-xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700"
                />
              </div>

              {/* Rich Editor Pane */}
              <div className="space-y-2 flex flex-col">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Contenido Enriquecido *</label>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-xs text-slate-400 font-mono">
                      {wordCount} palabras • {charCount} caracteres
                    </div>

                    <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg border border-slate-200 dark:border-slate-700">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditorLayout("edit")}
                        className={cn(
                          "rounded-md px-3 py-1 font-bold text-xs transition-all h-7",
                          editorLayout === "edit"
                            ? "bg-white dark:bg-slate-950 text-pink-600 dark:text-pink-400 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        )}
                      >
                        Escribir
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditorLayout("split")}
                        className={cn(
                          "rounded-md px-3 py-1 font-bold text-xs transition-all h-7",
                          editorLayout === "split"
                            ? "bg-white dark:bg-slate-950 text-pink-600 dark:text-pink-400 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        )}
                      >
                        Doble Panel
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditorLayout("preview")}
                        className={cn(
                          "rounded-md px-3 py-1 font-bold text-xs transition-all h-7",
                          editorLayout === "preview"
                            ? "bg-white dark:bg-slate-950 text-pink-600 dark:text-pink-400 shadow-sm"
                            : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        )}
                      >
                        Vista Previa
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Toolbar */}
                {(editorLayout === "edit" || editorLayout === "split") && (
                  <RichEditorToolbar
                    content={formContent}
                    onChange={setFormContent}
                    textareaRef={textareaRef}
                    onApplyTemplate={handleApplyTemplate}
                  />
                )}

                <div className={cn(
                  "grid gap-6 transition-all duration-300",
                  editorLayout === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
                )}>
                  {/* Write Pane */}
                  {(editorLayout === "edit" || editorLayout === "split") && (
                    <div className="flex flex-col space-y-1">
                      {editorLayout === "split" && (
                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Editor Markdown / HTML</span>
                      )}
                      <Textarea 
                        ref={textareaRef}
                        required
                        placeholder="# Titular principal&#10;&#10;Escribe tu boletín aquí. Puedes copiar (Ctrl+C) y pegar (Ctrl+V) o arrastrar imágenes directamente sin necesidad de tener una URL..."
                        value={formContent}
                        onChange={(e) => setFormContent(e.target.value)}
                        onPaste={handlePasteImage}
                        onDrop={handleDropImage}
                        className="rounded-2xl bg-slate-50/50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 font-mono text-sm resize-none h-[460px] focus-visible:ring-pink-500 focus-visible:border-pink-500 p-4 leading-relaxed"
                      />
                      <span className="text-[11px] text-slate-400 font-medium pt-1">
                        📷 <strong>Tip Pro</strong>: Puedes tomar una captura de pantalla o copiar cualquier imagen al portapapeles y pegarla directamente con <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-slate-200 dark:bg-slate-800 rounded border">Ctrl+V</kbd> o arrastrarla al editor.
                      </span>
                    </div>
                  )}

                  {/* Rich Preview Pane */}
                  {(editorLayout === "preview" || editorLayout === "split") && (
                    <div className="flex flex-col space-y-1">
                      {editorLayout === "split" && (
                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Vista Previa Enriquecida en Tiempo Real</span>
                      )}
                      <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-6 h-[460px] overflow-y-auto bg-white dark:bg-slate-900 shadow-inner">
                        {formContent ? (
                          <RichBulletinRenderer content={formContent} />
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-slate-400 italic space-y-2">
                            <Wand2 className="w-8 h-8 text-pink-400 animate-pulse" />
                            <span>Escribe en el editor para ver el renderizado en tiempo real...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* State and Broadcast controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-slate-50 dark:bg-slate-800/40 p-5 rounded-2xl border border-slate-100 dark:border-slate-800/60">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Estado de Publicación</label>
                  <Select value={formStatus} onValueChange={(val: "draft" | "published") => {
                    setFormStatus(val);
                    if (val === "draft") setFormSendEmail(false);
                  }}>
                    <SelectTrigger className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 font-bold">
                      <SelectValue placeholder="Estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">🟢 Publicado (Visible a Todos)</SelectItem>
                      <SelectItem value="draft">🟡 Borrador (Solo Admin)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between sm:justify-end gap-6">
                    <div className="flex flex-col text-right">
                      <span className={cn(
                        "text-sm font-bold transition-colors", 
                        formStatus === "published" ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-600"
                      )}>
                        Enviar Distribución por Correo
                      </span>
                      <span className="text-[11px] text-slate-400 max-w-[200px]">
                        Envía correos con seguimiento de lectura en tiempo real.
                      </span>
                    </div>
                    <Switch 
                      disabled={formStatus !== "published"}
                      checked={formSendEmail} 
                      onCheckedChange={setFormSendEmail}
                      className="data-[state=checked]:bg-pink-600"
                    />
                  </div>

                  {formSendEmail && formStatus === "published" && (
                    <div className="space-y-2 pt-2 border-t border-slate-200/60 dark:border-slate-700/60">
                      <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Audiencia Destino</label>
                      <Select value={formTargetAudience} onValueChange={setFormTargetAudience}>
                        <SelectTrigger className="rounded-xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 font-bold text-xs">
                          <SelectValue placeholder="Audiencia" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">🚀 Todos (Usuarios Registrados + Prospectos Marketing)</SelectItem>
                          <SelectItem value="users">👥 Solo Usuarios Registrados de la Plataforma</SelectItem>
                          <SelectItem value="marketing_leads">🎯 Solo Lista de Difusión (Prospectos Marketing)</SelectItem>
                          <SelectItem value="custom">✉️ Correos Específicos / Personalizados</SelectItem>
                        </SelectContent>
                      </Select>

                      {formTargetAudience === "custom" && (
                        <div className="space-y-1.5 pt-1">
                          <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">
                            Correos Específicos (Separados por comas o saltos de línea)
                          </label>
                          <Textarea
                            placeholder="ejemplo1@abogados.com, cliente2@firma.co&#10;contacto3@justicia.org"
                            value={formCustomEmails}
                            onChange={(e) => setFormCustomEmails(e.target.value)}
                            className="rounded-xl text-xs font-mono bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 h-20"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="p-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsEditorOpen(false)}
                disabled={saving}
                className="rounded-xl px-6 font-bold border-slate-200 dark:border-slate-700"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white font-bold rounded-xl px-8 ml-2 flex items-center gap-2 shadow-lg shadow-pink-200 dark:shadow-none"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4.5 w-4.5 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Guardar y Publicar
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cover Gallery Modal with Unsplash Integration */}
      <UnsplashStockGalleryModal
        isOpen={isCoverGalleryOpen}
        onClose={() => setIsCoverGalleryOpen(false)}
        onSelectCoverImage={(url) => setFormImageUrl(url)}
        onInsertMarkdownImage={(url, title) => {
          const textarea = textareaRef.current;
          const markdownImg = `\n\n![${title}](${url})\n\n`;
          setFormContent((prev) => {
            const start = textarea?.selectionStart ?? prev.length;
            return prev.slice(0, start) + markdownImg + prev.slice(start);
          });
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={(open) => !deleting && setIsDeleteOpen(open)}>
        <DialogContent className="sm:max-w-[425px] rounded-[2rem] border-none shadow-2xl p-8 bg-white dark:bg-slate-900">
          <DialogHeader>
            <div className="w-14 h-14 bg-rose-50 dark:bg-rose-950/20 rounded-2xl flex items-center justify-center mb-4">
              <Trash2 className="h-6 w-6 text-rose-600" />
            </div>
            <DialogTitle className="text-2xl font-black text-slate-900 dark:text-white">
              ¿Eliminar este boletín?
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400 text-md py-2 leading-relaxed">
              Esta acción es irreversible y eliminará de forma permanente el boletín <strong>{bulletinToDelete?.title}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex gap-3 sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteOpen(false)}
              disabled={deleting}
              className="flex-1 rounded-xl py-6 font-bold border-slate-200 dark:border-slate-700"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl py-6 shadow-lg shadow-rose-100 dark:shadow-none"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Render User Feed Section
  function renderFeedSection() {
    return (
      <div className="space-y-6">
        {/* Filters and search */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-sm">
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            {["Todos", ...CATEGORIES].map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "ghost"}
                onClick={() => setSelectedCategory(cat)}
                className={cn(
                  "rounded-xl px-4 py-2 font-bold transition-all text-xs sm:text-sm",
                  selectedCategory === cat 
                    ? "bg-pink-600 text-white hover:bg-pink-700 shadow-md shadow-pink-100 dark:shadow-none" 
                    : "text-slate-500 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/10"
                )}
              >
                {cat}
              </Button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar novedades..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-5.5 rounded-xl border-slate-200 dark:border-slate-700 text-sm focus:ring-pink-500 focus:border-pink-500 bg-slate-50/50 dark:bg-slate-950/50"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="h-10 w-10 text-pink-500 animate-spin mb-4" />
            <p className="text-slate-500 dark:text-slate-400 font-medium">Buscando boletines...</p>
          </div>
        ) : filteredBulletins.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBulletins.map((bulletin) => (
              <Card 
                key={bulletin.id}
                className="group bg-white dark:bg-slate-900 rounded-3xl border border-gray-100 dark:border-slate-800 overflow-hidden shadow-sm hover:shadow-2xl hover:border-pink-200 dark:hover:border-pink-900/50 transition-all duration-300 flex flex-col cursor-pointer"
                onClick={() => handleOpenReader(bulletin)}
              >
                {/* Card Cover Image */}
                <div className="h-52 overflow-hidden relative">
                  <img 
                    src={bulletin.image_url || DEFAULT_COVER} 
                    alt={bulletin.title}
                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className={cn("border-none px-3 py-1 text-[11px] font-bold shadow-md", getCategoryBadgeClass(bulletin.category))}>
                      {bulletin.category}
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-3 flex-1">
                    <div className="flex items-center gap-4 text-[11px] text-slate-400 font-medium">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5 text-pink-500" />
                        <span>
                          {bulletin.published_at 
                            ? new Date(bulletin.published_at).toLocaleDateString("es-ES") 
                            : new Date(bulletin.created_at).toLocaleDateString("es-ES")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-pink-500" />
                        <span>{bulletin.read_time}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-extrabold text-slate-900 dark:text-slate-100 group-hover:text-pink-600 transition-colors line-clamp-2 leading-snug">
                      {bulletin.title}
                    </h3>

                    <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-3 leading-relaxed">
                      {bulletin.excerpt || "Haz clic para leer la noticia completa..."}
                    </p>
                  </div>

                  <div className="flex items-center justify-between pt-5 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                        {bulletin.author_name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-xs text-slate-600 dark:text-slate-300 font-semibold truncate max-w-[120px]">
                        {bulletin.author_name}
                      </span>
                    </div>

                    <span className="text-pink-600 hover:text-pink-700 text-xs font-extrabold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                      Leer Noticia
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-dashed border-gray-300 dark:border-slate-800 p-16 text-center shadow-sm flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-2xl bg-pink-50 dark:bg-pink-950/30 flex items-center justify-center mb-4">
              <Newspaper className="h-8 w-8 text-pink-500" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
              No se encontraron boletines
            </h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-md mb-6 text-sm">
              No hay publicaciones publicadas en esta categoría. Puedes hacer clic abajo para publicar la noticia oficial de Calculadoras Jurídicas.
            </p>
            {isAdmin && (
              <Button
                onClick={handlePrefillCalculatorsPost}
                className="bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold px-6 py-5 rounded-xl shadow-lg flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Publicar Noticia de Lanzamiento de Calculadoras
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Render Admin Management Section
  function renderAdminSection() {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-3xl border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="h-10 w-10 text-pink-500 animate-spin mb-4" />
            <p className="text-slate-500 dark:text-slate-400">Cargando boletines...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 text-xs font-bold text-slate-400 uppercase">
                  <th className="p-6">Título</th>
                  <th className="p-6">Categoría</th>
                  <th className="p-6">Estado</th>
                  <th className="p-6">Métricas de Lectura</th>
                  <th className="p-6">Fecha Publicación</th>
                  <th className="p-6">Autor</th>
                  <th className="p-6 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm text-slate-700 dark:text-slate-300">
                {bulletins.length > 0 ? (
                  bulletins.map((bulletin) => (
                    <tr key={bulletin.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-850/50 transition-colors">
                      <td className="p-6 font-bold text-slate-900 dark:text-white max-w-xs truncate">
                        {bulletin.title}
                      </td>
                      <td className="p-6">
                        <Badge className={cn("border px-2.5 py-0.5 text-xs font-medium shadow-none", getCategoryBadgeClass(bulletin.category))}>
                          {bulletin.category}
                        </Badge>
                      </td>
                      <td className="p-6">
                        {bulletin.status === "published" ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-400 font-semibold shadow-none flex items-center w-fit gap-1">
                            <Globe className="w-3 h-3" />
                            Publicado
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-500 border border-slate-100 dark:bg-slate-800 dark:text-slate-400 font-semibold shadow-none flex items-center w-fit gap-1">
                            <FileText className="w-3 h-3" />
                            Borrador
                          </Badge>
                        )}
                      </td>
                      <td className="p-6">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAnalyticsBulletinId(bulletin.id);
                            setAnalyticsBulletinTitle(bulletin.title);
                            setIsAnalyticsModalOpen(true);
                          }}
                          className="rounded-xl text-xs font-bold border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 h-8 gap-1.5"
                        >
                          <BarChart3 className="w-3.5 h-3.5" />
                          Ver Lectores
                        </Button>
                      </td>
                      <td className="p-6 text-xs text-slate-400">
                        {bulletin.published_at 
                          ? new Date(bulletin.published_at).toLocaleString("es-ES", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit"
                            })
                          : "N/A"}
                      </td>
                      <td className="p-6 font-semibold">
                        {bulletin.author_name}
                      </td>
                      <td className="p-6 text-right">
                        <div className="flex gap-2 justify-end">
                          {bulletin.status === "published" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenBroadcastModal(bulletin)}
                              title="Enviar Campaña por Correo"
                              className="h-9 w-9 rounded-xl text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenReader(bulletin)}
                            title="Previsualizar"
                            className="h-9 w-9 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                          >
                            <Eye className="w-4.5 h-4.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenEdit(bulletin)}
                            title="Editar"
                            className="h-9 w-9 rounded-xl text-slate-400 hover:text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-900/10"
                          >
                            <Edit3 className="w-4.5 h-4.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(bulletin)}
                            title="Eliminar"
                            className="h-9 w-9 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/10"
                          >
                            <Trash2 className="w-4.5 h-4.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-slate-400 italic">
                      No hay ningún boletín registrado. Haz clic en "Nuevo Boletín" para crear uno.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Modals */}
        <MarketingContactsModal
          isOpen={isMarketingModalOpen}
          onClose={() => setIsMarketingModalOpen(false)}
        />

        <BulletinAnalyticsModal
          bulletinId={analyticsBulletinId}
          bulletinTitle={analyticsBulletinTitle}
          isOpen={isAnalyticsModalOpen}
          onClose={() => setIsAnalyticsModalOpen(false)}
        />

        {/* Modal Enviar Campaña de Difusión Directa */}
        <Dialog open={isBroadcastModalOpen} onOpenChange={(open) => !broadcasting && setIsBroadcastModalOpen(open)}>
          <DialogContent className="sm:max-w-md rounded-2xl p-6 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-2xl">
            <DialogHeader>
              <div className="h-10 w-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-2">
                <Send className="h-5 w-5" />
              </div>
              <DialogTitle className="text-lg font-bold text-slate-900 dark:text-white">
                Enviar Campaña de Difusión por Correo
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
                Boletín: <strong>{broadcastBulletin?.title}</strong>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                  Seleccionar Audiencia Destino *
                </label>
                <Select
                  value={broadcastAudience}
                  onValueChange={(val: "marketing_leads" | "users" | "all" | "custom") => setBroadcastAudience(val)}
                >
                  <SelectTrigger className="rounded-xl bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 font-bold text-xs">
                    <SelectValue placeholder="Audiencia" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing_leads">🎯 Solo Lista de Difusión (Leads / Prospectos)</SelectItem>
                    <SelectItem value="users">👥 Solo Usuarios Registrados de la Plataforma</SelectItem>
                    <SelectItem value="all">🚀 Todos (Usuarios Registrados + Lista de Difusión)</SelectItem>
                    <SelectItem value="custom">✉️ Correos Específicos / Personalizados</SelectItem>
                  </SelectContent>
                </Select>

                {broadcastAudience === "custom" && (
                  <div className="space-y-1.5 pt-2">
                    <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      Ingresa los Correos Específicos (Separados por comas o líneas) *
                    </label>
                    <Textarea
                      placeholder="abogado1@ejemplo.com, cliente2@firmalegal.co&#10;contacto3@justicia.org"
                      value={broadcastCustomEmails}
                      onChange={(e) => setBroadcastCustomEmails(e.target.value)}
                      className="rounded-xl text-xs font-mono bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 h-24"
                    />
                  </div>
                )}
              </div>

              <div className="p-3.5 rounded-xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/60 dark:border-purple-800/50 text-xs text-purple-900 dark:text-purple-300 space-y-1">
                <p className="font-bold flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                  Seguimiento en Tiempo Real
                </p>
                <p className="text-[11px] text-purple-700 dark:text-purple-400 leading-relaxed">
                  Cada correo incluirá un píxel de seguimiento invisible para rastrear quién abre el correo desde el botón <strong>"Ver Lectores"</strong>.
                </p>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0 pt-2 border-t border-slate-100 dark:border-slate-800">
              <Button
                variant="outline"
                disabled={broadcasting}
                onClick={() => setIsBroadcastModalOpen(false)}
                className="rounded-xl text-xs font-bold border-slate-200 dark:border-slate-700"
              >
                Cancelar
              </Button>
              <Button
                disabled={broadcasting}
                onClick={handleExecuteBroadcast}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs px-5 shadow-md shadow-purple-500/20"
              >
                {broadcasting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    Iniciando Envío...
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    Enviar Campaña
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}
